/**
 * Phase HR-JOIN-001 — Employee Joining Workflow API client.
 *
 * Backend service: app/api/routers/joining.py (mounted at /api/v1/joining).
 */
import apiClient from '@/shared/api/client'

// ─── Types ──────────────────────────────────────────────────────────────

export const JOINING_STATUSES = [
  'DRAFT', 'DOCUMENT_PENDING', 'DOCUMENT_RECEIVED', 'VERIFICATION_PENDING',
  'VERIFIED', 'EMP_CODE_GENERATED', 'PAYROLL_PENDING', 'STATUTORY_PENDING',
  'BIOMETRIC_PENDING', 'INDUCTION_PENDING', 'APPROVAL_PENDING',
  'ACTIVE', 'HOLD', 'REJECTED',
] as const
export type JoiningStatus = (typeof JOINING_STATUSES)[number]

export const JOINING_EMPLOYEE_TYPES = [
  'PRODUCTION_WORKER', 'MAINTENANCE_TECHNICIAN', 'QC_LAB', 'STORE',
  'SECURITY', 'HOUSEKEEPING', 'STAFF_OFFICE', 'CONTRACT_WORKER',
  'TRAINEE_APPRENTICE',
] as const
export type JoiningEmployeeType = (typeof JOINING_EMPLOYEE_TYPES)[number]

export type PhotoStatus = 'NOT_UPLOADED' | 'UPLOADED' | 'VERIFIED' | 'REJECTED' | 'REUPLOAD_REQUIRED'
export type DocStatus = 'PENDING' | 'RECEIVED' | 'VERIFIED' | 'REJECTED' | 'EXPIRED'
export type ChecklistItemStatus = 'PENDING' | 'DONE' | 'NA' | 'WAIVED'

export interface JoiningListItem {
  id: string
  emp_code: string | null
  full_name: string
  plant_name: string
  department_name: string
  designation: string | null
  joining_date: string | null
  joining_status: JoiningStatus
  joining_employee_type: JoiningEmployeeType | null
  photo_status: PhotoStatus
  completion_percent: number
  is_active: boolean
}

export interface PaginatedJoining {
  items: JoiningListItem[]
  total: number
  page: number
  size: number
  pages: number
}

export interface JoiningDraftCreate {
  first_name: string
  last_name?: string
  middle_name?: string
  father_or_husband_name?: string
  gender: string
  date_of_birth: string
  phone: string
  plant_id: string
  department_id: string
  joining_date: string
  category: string
  joining_employee_type: JoiningEmployeeType
  joining_notes?: string
}

export interface DocumentRow {
  id: string
  employee_id: string
  doc_type: string
  doc_number: string | null
  doc_name: string | null
  file_path: string | null
  issued_date: string | null
  expiry_date: string | null
  status: DocStatus
  created_by: string | null
  verified_at: string | null
  rejection_reason: string | null
  remarks?: string | null
  file_size?: number | null
  mime_type?: string | null
  created_at: string
}

export interface NomineeRow {
  id: string
  employee_id: string
  nominee_name: string
  relationship: string
  dob: string | null
  address: string | null
  phone: string | null
  share_percent: number
  nomination_type: string
  is_primary: boolean
}

export interface EmergencyContactRow {
  id: string
  employee_id: string
  contact_name: string
  relationship: string
  phone: string
  alt_phone: string | null
  is_primary: boolean
}

export interface ChecklistItemRow {
  id: string
  employee_id: string
  item_code: string
  item_name: string
  category: string
  owner_role: string
  is_required: boolean
  required_before_activation: boolean
  display_order: number
  status: ChecklistItemStatus
  completed_at: string | null
  waived_reason: string | null
  remarks: string | null
}

export interface AuditLogRow {
  id: string
  employee_id: string
  event_type: string
  from_status: string | null
  to_status: string | null
  field_name: string | null
  old_value: string | null
  new_value: string | null
  notes: string | null
  performed_by: string | null
  performed_at: string
}

export interface ActivationCheckItem {
  check_code: string
  check_label: string
  passed: boolean
  detail: string | null
}

export interface ActivationCheckResponse {
  employee_id: string
  overall_passed: boolean
  blocked_reasons: string[]
  checks: ActivationCheckItem[]
  completion_percent: number
}

export interface JoiningDetail {
  id: string
  emp_code: string | null
  joining_status: JoiningStatus
  joining_employee_type: JoiningEmployeeType | null
  full_name: string
  is_active: boolean
  plant_id: string
  department_id: string
  employee: Record<string, unknown>
  documents: DocumentRow[]
  nominees: NomineeRow[]
  emergency_contacts: EmergencyContactRow[]
  checklist: ChecklistItemRow[]
  audit_log: AuditLogRow[]
  completion_percent: number
}

// ─── Endpoints ──────────────────────────────────────────────────────────

export const joiningApi = {
  list: async (params: {
    status?: JoiningStatus
    employee_type?: JoiningEmployeeType
    plant_id?: string
    department_id?: string
    search?: string
    include_active?: boolean
    page?: number
    size?: number
  }): Promise<PaginatedJoining> => {
    const { data } = await apiClient.get<PaginatedJoining>('/joining', { params })
    return data
  },

  create: async (body: JoiningDraftCreate): Promise<{ id: string; joining_status: JoiningStatus; emp_code: string }> => {
    const { data } = await apiClient.post('/joining', body)
    return data
  },

  detail: async (id: string): Promise<JoiningDetail> => {
    const { data } = await apiClient.get<JoiningDetail>(`/joining/${id}`)
    return data
  },

  update: async (id: string, body: Record<string, unknown>): Promise<{ id: string; joining_status: JoiningStatus }> => {
    const { data } = await apiClient.patch(`/joining/${id}`, body)
    return data
  },

  transition: async (id: string, to_status: JoiningStatus, notes?: string) => {
    const { data } = await apiClient.post(`/joining/${id}/transition`, { to_status, notes })
    return data
  },

  generateEmpCode: async (id: string): Promise<{ id: string; emp_code: string }> => {
    const { data } = await apiClient.post(`/joining/${id}/generate-emp-code`)
    return data
  },

  activationCheck: async (id: string): Promise<ActivationCheckResponse> => {
    const { data } = await apiClient.get<ActivationCheckResponse>(`/joining/${id}/activation-check`)
    return data
  },

  // GOV-001 P5 — activation now routes through HR Manager -> Plant Head
  // approval; the endpoint returns a PENDING_APPROVAL envelope.
  activate: async (id: string): Promise<{ approval_status?: string; message?: string; activated?: boolean; emp_code?: string; effects?: string[] }> => {
    const { data } = await apiClient.post(`/joining/${id}/activate`)
    return data
  },

  addDocument: async (id: string, body: Partial<DocumentRow> & { doc_type: string }) => {
    const { data } = await apiClient.post<DocumentRow>(`/joining/${id}/documents`, body)
    return data
  },

  verifyDocument: async (docId: string, status: 'VERIFIED' | 'REJECTED', rejection_reason?: string) => {
    const { data } = await apiClient.patch<DocumentRow>(`/joining/documents/${docId}/verify`, {
      status,
      rejection_reason,
    })
    return data
  },

  addNominee: async (id: string, body: Partial<NomineeRow> & { nominee_name: string; relationship: string }) => {
    const { data } = await apiClient.post<NomineeRow>(`/joining/${id}/nominees`, body)
    return data
  },

  addEmergencyContact: async (id: string, body: Partial<EmergencyContactRow> & { contact_name: string; relationship: string; phone: string }) => {
    const { data } = await apiClient.post<EmergencyContactRow>(`/joining/${id}/emergency-contacts`, body)
    return data
  },

  updateChecklistItem: async (
    itemId: string,
    status: ChecklistItemStatus,
    waived_reason?: string,
    remarks?: string,
  ) => {
    const { data } = await apiClient.patch(`/joining/checklist/${itemId}`, {
      status,
      waived_reason,
      remarks,
    })
    return data
  },

  recordPhoto: async (id: string, photo_path: string, source = 'FILE_UPLOAD') => {
    const { data } = await apiClient.post(`/joining/${id}/photo`, null, {
      params: { photo_path, source },
    })
    return data
  },

  verifyPhoto: async (id: string, verified: boolean, rejection_reason?: string) => {
    const { data } = await apiClient.patch(`/joining/${id}/photo/verify`, {
      verified,
      rejection_reason,
    })
    return data
  },
}
